import React, { useMemo, useEffect, useRef } from 'react';
import { Text, StyleSheet, ViewStyle, TextStyle, Platform, View } from 'react-native';
import { parseUrls, openUrl } from '../Utils/textUtils';
import { theme } from '../constants/theme';

interface TextWithLinksProps {
  text: string;
  textStyle?: TextStyle;
  linkStyle?: TextStyle;
  containerStyle?: ViewStyle;
}

const TextWithLinks: React.FC<TextWithLinksProps> = ({
  text,
  textStyle,
  linkStyle,
  containerStyle
}) => {
  const segments = useMemo(() => parseUrls(text), [text]);
  const containerRef = useRef<any>(null);
  
  // On web, attach click handlers to link elements after render
  useEffect(() => {
    if (Platform.OS === 'web' && containerRef.current) {
      const element = containerRef.current;
      if (element && typeof element.querySelectorAll === 'function') {
        const links = element.querySelectorAll('[data-link-url]');
        links.forEach((link: any) => {
          const url = link.getAttribute('data-link-url');
          if (url) {
            link.addEventListener('click', (e: any) => {
              e.preventDefault();
              e.stopPropagation();
              openUrl(url);
            });
            link.style.cursor = 'pointer';
            link.style.color = theme.colors.primary;
            link.style.textDecoration = 'underline';
          }
        });
      }
    }
  }, [segments]);
  
  if (segments.length === 0) {
    return <Text style={textStyle}>{text}</Text>;
  }
  
  // If only one text segment (no URLs), return simple text
  if (segments.length === 1 && segments[0].type === 'text') {
    return <Text style={textStyle}>{segments[0].content}</Text>;
  }
  
  // On web, render as HTML string with anchor tags for proper link functionality
  if (Platform.OS === 'web') {
    // Build HTML content with anchor tags
    const htmlParts: React.ReactNode[] = [];
    segments.forEach((segment, index) => {
      if (segment.type === 'url' && segment.url) {
        // Create anchor element using React.createElement
        htmlParts.push(
          React.createElement(
            'a',
            {
              key: `link-${index}`,
              href: segment.url,
              target: '_blank',
              rel: 'noopener noreferrer',
              onClick: (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                if (__DEV__) {
                  console.log('[TextWithLinks] Opening URL:', segment.url);
                }
                openUrl(segment.url!);
              },
              style: {
                color: theme.colors.primary,
                textDecoration: 'underline',
                cursor: 'pointer',
                display: 'inline',
                ...StyleSheet.flatten(linkStyle),
              },
            },
            segment.content
          )
        );
      } else {
        htmlParts.push(
          React.createElement(
            'span',
            {
              key: `text-${index}`,
              style: {
                display: 'inline',
                ...StyleSheet.flatten(textStyle),
              },
            },
            segment.content
          )
        );
      }
    });

    return (
      <View 
        ref={containerRef}
        style={[styles.container, containerStyle, { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }]}
      >
        {htmlParts}
      </View>
    );
  }
  
  // On native, use Text with onPress
  return (
    <View style={[styles.container, containerStyle]}>
      <Text style={textStyle}>
        {segments.map((segment, index) => {
          if (segment.type === 'url' && segment.url) {
            return (
              <Text
                key={index}
                style={[styles.link, linkStyle]}
                onPress={() => openUrl(segment.url!)}
                onPress={() => openUrl(segment.url!)}
              >
                {segment.content}
              </Text>
            );
          } else {
            return (
              <Text key={index}>
                {segment.content}
              </Text>
            );
          }
        })}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexWrap: 'wrap',
  },
  link: {
    color: theme.colors.primary,
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      // @ts-ignore - web-specific styles
      WebkitUserSelect: 'none',
      userSelect: 'none',
    }),
  },
});

export default TextWithLinks;

